import { ApiError } from '@/api/client'
import { useCreateIngredient, useIngredientSearch } from '@/api/hooks'
import type { ExchangeIngredient, FoodSearchResult, Ingredient } from '@/api/types'
import { cn } from '@/lib/cn'
import { formatGrams, formatMacro } from '@/lib/format'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Overlay'
import { FormError } from '@/components/ui/Feedback'
import { Input, NumberInput, UnitInput } from '@/components/ui/Field'
import { FoodLookupDialog } from '@/features/ingredients/FoodLookupDialog'
import { BookOpen, Check, Plus, Search, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * Combobox over the ingredient library that can create entries inline.
 *
 * Choosing "Create …" opens a dialog, saves the ingredient through the API and
 * selects it here — the recipe form never navigates away or loses its state.
 * This is the flow that previously meant leaving the recipe, adding the
 * ingredient, and starting again.
 */
export function IngredientPicker({
  excludedIds,
  onSelect,
  autoFocus = false,
  initialTerm = '',
}: {
  excludedIds: Set<number>
  onSelect: (ingredient: Ingredient | null) => void
  autoFocus?: boolean
  /** Pre-filled search, e.g. the name an imported line used. */
  initialTerm?: string
}) {
  const [term, setTerm] = useState(initialTerm)
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const [creating, setCreating] = useState<string | null>(null)
  const container = useRef<HTMLDivElement>(null)

  /** Changing the search term always restarts the keyboard cursor at the top. */
  function runSearch(next: string) {
    setTerm(next)
    setHighlighted(0)
    setOpen(true)
  }

  const search = useIngredientSearch(term.trim())

  const existing = useMemo(
    () => (search.data?.items ?? []).filter((item) => !excludedIds.has(item.id)),
    [search.data, excludedIds],
  )

  const exact = useMemo(
    () => existing.find((item) => item.name === term.trim().toLowerCase()),
    [existing, term],
  )

  const canCreate =
    term.trim().length > 0 && !exact && !search.isFetching

  const options: Array<{ kind: 'ingredient'; ingredient: Ingredient } | { kind: 'create' }> = [
    ...existing.map((ingredient) => ({ kind: 'ingredient' as const, ingredient })),
    ...(canCreate ? [{ kind: 'create' as const } as const] : []),
  ]

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function choose(index: number) {
    const option = options[index]
    if (!option) return
    if (option.kind === 'create') {
      setCreating(term.trim())
      return
    }
    onSelect(option.ingredient)
    runSearch('')
    setOpen(false)
  }

  return (
    <div ref={container} className="relative">
      <div className="flex gap-1.5">
        <div className="relative min-w-0 flex-1">
          <Search
            size={15}
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
          />
          <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-label="Search ingredients"
          placeholder="Search ingredients…"
          value={term}
          autoFocus={autoFocus}
          onFocus={() => setOpen(true)}
          onChange={(event) => runSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setHighlighted((current) => Math.min(current + 1, options.length - 1))
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setHighlighted((current) => Math.max(current - 1, 0))
            } else if (event.key === 'Enter') {
              event.preventDefault()
              choose(highlighted)
            } else if (event.key === 'Escape') {
              setOpen(false)
            }
          }}
          className="h-10 w-full rounded-[var(--radius-field)] border border-line-strong bg-surface pr-3 pl-9 text-sm text-ink placeholder:text-ink-faint/70 hover:border-ink-faint focus:border-accent"
        />
        </div>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          title="Create ingredient"
          aria-label="Create ingredient"
          disabled={!term.trim()}
          onClick={() => setCreating(term.trim())}
        >
          <Plus size={16} />
        </Button>
      </div>

      {open && options.length > 0 ? (
        <ul
          role="listbox"
          className="scroll-slim absolute left-0 top-full z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-[var(--radius-field)] border border-line bg-surface py-1 shadow-raised"
        >
          {options.map((option, index) => {
            const selected = index === highlighted
            if (option.kind === 'create') {
              return (
                <li key="create">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => choose(index)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      selected ? 'bg-accent-soft text-accent-ink' : 'text-ink',
                    )}
                  >
                    <Plus size={14} aria-hidden />
                    Create “{term.trim().toLowerCase()}”
                  </button>
                </li>
              )
            }
            const ingredient = option.ingredient
            const tracked =
              ingredient.calories !== null || ingredient.protein !== null
            return (
              <li key={ingredient.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => choose(index)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                    selected ? 'bg-accent-soft text-accent-ink' : 'text-ink',
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{ingredient.name}</span>
                    {/* A known weight for one unit, so a recipe can count this
                        ingredient the way a cookbook would. */}
                    {ingredient.grams_per_unit && ingredient.conversion_unit ? (
                      <span className="block truncate text-xs text-ink-faint">
                        {formatGrams(ingredient.grams_per_unit)} per {ingredient.conversion_unit}
                      </span>
                    ) : null}
                  </span>
                  <span className="tabular shrink-0 text-xs text-ink-faint">
                    {tracked && ingredient.calories !== null
                      ? `${formatMacro(ingredient.calories)} kcal/100g`
                      : 'no macros'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}

      <CreateIngredientDialog
        initialName={creating ?? ''}
        open={creating !== null}
        onClose={() => setCreating(null)}
        onCreated={(ingredient) => {
          setCreating(null)
          onSelect(ingredient)
          setTerm('')
        }}
      />
    </div>
  )
}

export function CreateIngredientDialog({
  open,
  initialName,
  initialValues,
  onClose,
  onCreated,
}: {
  open: boolean
  initialName: string
  /** Macros to start from, e.g. the ones an import file proposed. */
  initialValues?: ExchangeIngredient | null | undefined
  onClose: () => void
  onCreated: (ingredient: Ingredient) => void
}) {
  const create = useCreateIngredient()
  const [name, setName] = useState(initialName)
  const [trackMacros, setTrackMacros] = useState(false)
  const [calories, setCalories] = useState<number | null>(null)
  const [protein, setProtein] = useState<number | null>(null)
  const [carbs, setCarbs] = useState<number | null>(null)
  const [fat, setFat] = useState<number | null>(null)
  const [fiber, setFiber] = useState<number | null>(null)
  const [conversionUnit, setConversionUnit] = useState('')
  const [gramsPerUnit, setGramsPerUnit] = useState<number | null>(null)
  const [lookingUp, setLookingUp] = useState(false)

  // Re-seed the form each time the dialog opens for a new name.
  useEffect(() => {
    if (!open) return
    const values = initialValues
    setName(initialName)
    setTrackMacros(
      [values?.calories, values?.protein, values?.carbs, values?.fat, values?.fiber].some(
        (value) => value !== null && value !== undefined,
      ),
    )
    setCalories(values?.calories ?? null)
    setProtein(values?.protein ?? null)
    setCarbs(values?.carbs ?? null)
    setFat(values?.fat ?? null)
    setFiber(values?.fiber ?? null)
    setConversionUnit(values?.conversion_unit ?? '')
    setGramsPerUnit(values?.grams_per_unit ?? null)
    setLookingUp(false)
    create.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialName])

  /** Copy a looked-up food into the form. Still not saved until submit. */
  function applyLookup(food: FoodSearchResult) {
    setLookingUp(false)
    setName(food.description)
    setTrackMacros(true)
    setCalories(food.calories)
    setProtein(food.protein)
    setCarbs(food.carbs)
    setFat(food.fat)
    setFiber(food.fiber)
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const payload: Record<string, unknown> = { name }
    if (trackMacros) {
      payload.calories = calories
      payload.protein = protein
      payload.carbs = carbs
      payload.fat = fat
      payload.fiber = fiber
    }
    if (conversionUnit.trim() && gramsPerUnit !== null) {
      payload.conversion_unit = conversionUnit.trim()
      payload.grams_per_unit = gramsPerUnit
    }
    const created = await create.mutateAsync(payload)
    onCreated(created)
  }

  const error = create.error
  const message =
    error instanceof ApiError ? error.message : error ? 'Could not create the ingredient.' : null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New ingredient"
      description="Added to the library and selected straight away."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!name.trim()}
            onClick={(event) => {
              event.preventDefault()
              void submit(event as unknown as React.FormEvent)
            }}
          >
            <Check size={15} aria-hidden />
            Add ingredient
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <FormError>{message}</FormError>
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          data-autofocus
          autoFocus
          required
        />

        <div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLookingUp(true)}
          >
            <BookOpen size={14} aria-hidden />
            Look up on USDA FoodData Central
          </Button>
          <p className="mt-1.5 text-micro text-pretty text-ink-faint">
            Fills the name and the macros. The ingredient is added when you
            press Add ingredient.
          </p>
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 rounded-[var(--radius-field)] border border-line bg-surface-sunken px-3.5 py-3">
          <input
            type="checkbox"
            checked={trackMacros}
            onChange={(event) => setTrackMacros(event.target.checked)}
            className="mt-0.5 size-4 rounded"
          />
          <span className="text-label text-pretty text-ink-soft">
            <span className="block font-medium text-ink">Track macros for this ingredient</span>
            Leave this off for spices, garnishes and anything else you do not count. It
            simply will not contribute to a recipe's nutrition.
          </span>
        </label>

        {trackMacros ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberInput
              label="Calories"
              value={calories}
              onValueChange={setCalories}
              suffix="per 100g"
              min={0}
            />
            <NumberInput
              label="Protein"
              value={protein}
              onValueChange={setProtein}
              suffix="g per 100g"
              min={0}
              step={0.1}
            />
            <NumberInput
              label="Carbs"
              value={carbs}
              onValueChange={setCarbs}
              suffix="g per 100g"
              min={0}
              step={0.1}
            />
            <NumberInput
              label="Fat"
              value={fat}
              onValueChange={setFat}
              suffix="g per 100g"
              min={0}
              step={0.1}
            />
            <NumberInput
              label="Fibre"
              value={fiber}
              onValueChange={setFiber}
              suffix="g per 100g"
              min={0}
              step={0.1}
            />
          </div>
        ) : null}

        {/* Optional. Only ever a suggestion the recipe form offers to apply. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <UnitInput
            label="Counted by"
            value={conversionUnit}
            onChange={(event) => setConversionUnit(event.target.value)}
            placeholder="can, clove, slice…"
          />
          <NumberInput
            label="Grams each"
            value={gramsPerUnit}
            onValueChange={setGramsPerUnit}
            placeholder="400"
            min={0}
          />
        </div>
        <p className="text-micro text-pretty text-ink-faint">
          Optional. Lets a recipe fill in grams when you count this ingredient by the unit.
        </p>

        {/* The submit button lives in the dialog footer; keep Enter working. */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>

      {/* Sits on top of this dialog and never touches the recipe form, which is
          the whole point: looking an ingredient up must not cost the recipe.
          Mounted only while open, so it re-seeds from the name typed here. */}
      {lookingUp ? (
        <FoodLookupDialog
          open
          query={name}
          onClose={() => setLookingUp(false)}
          onSelect={applyLookup}
        />
      ) : null}
    </Dialog>
  )
}

export function RemoveLineButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Remove ingredient"
      title="Remove from this recipe"
      className="grid size-8 shrink-0 place-items-center rounded-[var(--radius-field)] text-ink-faint hover:bg-critical-soft hover:text-critical"
    >
      <X size={15} aria-hidden />
    </button>
  )
}
